import { useState } from 'react';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { brandInfo, milestones, testimonials, faqs } from '@/mocks/brand';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      return;
    }

    const textarea = formData.get('message') as string;
    if (textarea && textarea.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500个字符。');
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      formData.delete('company_alt');
      const response = await fetch('https://readdy.ai/api/form/d9qq8s4nlsngrm4lm3t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brandInfo.name,
    url: 'https://guaguabase.com',
    description: brandInfo.description,
    telephone: brandInfo.phone,
    email: brandInfo.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '农展馆南路12号瓜瓜大厦',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '09:00',
        closes: '21:00',
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="瓜瓜基地成立以来，始终坚持产地直采，为消费者提供最新鲜的时令瓜果。了解瓜瓜基地的品牌故事、品质保障和联系方式。"
        keywords="瓜瓜基地,瓜瓜基地品牌,瓜瓜基地联系方式,瓜瓜基地品质保障"
        canonicalUrl="https://guaguabase.com/about"
      />
      <JsonLd data={orgSchema} />
      <JsonLd data={faqSchema} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18 bg-background-50">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        {/* Brand Story */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-5">
                  {brandInfo.slogan}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {brandInfo.description}
                </p>
                <div className="flex items-center gap-2 text-sm text-foreground-500">
                  <i className="ri-phone-line text-primary-500"></i>
                  <span>{brandInfo.phone}</span>
                  <span className="text-foreground-300 mx-2">|</span>
                  <i className="ri-mail-line text-primary-500"></i>
                  <span>{brandInfo.email}</span>
                </div>
              </div>
              <div className="flex-1 w-full">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20beautiful%20farmland%20with%20green%20melon%20vines%20growing%20in%20neat%20rows%20under%20warm%20golden%20sunlight%2C%20farmers%20working%20happily%2C%20rustic%20wooden%20crates%20of%20fresh%20melons%2C%20atmospheric%20farm%20photography%20with%20warm%20earthy%20tones%2C%20lifestyle%20agriculture%20scene&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="瓜瓜基地产地实拍"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">品牌历程</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                从2018年创立至今，每一步都走得踏实坚定
              </p>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div key={index} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className={`pl-10 md:pl-0 md:pt-0 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                        <div className="hidden md:block">
                          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-sm font-bold mb-2">
                            {item.year}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">用户口碑</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                听听真实用户怎么说，好品质口碑相传
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.location}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < t.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{t.content}</p>
                  <span className="text-xs text-foreground-400">购买产品：{t.productName}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">常见问题</h2>
              <p className="text-sm md:text-base text-foreground-500">关于瓜瓜基地，你想知道的都在这里</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <i
                      className={`text-lg text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                        openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">联系我们</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                有任何问题或建议，欢迎随时联系我们
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-1 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                    <i className="ri-phone-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电话咨询</h4>
                    <p className="text-sm text-foreground-600">{brandInfo.phone}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{brandInfo.workingHours}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                    <i className="ri-mail-line text-xl text-accent-500"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h4>
                    <p className="text-sm text-foreground-600">{brandInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-secondary-500"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h4>
                    <p className="text-sm text-foreground-600">{brandInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                    <i className="ri-wechat-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                    <p className="text-sm text-foreground-600">搜索关注：{brandInfo.wechat}</p>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form onSubmit={handleContactSubmit} data-readdy-form="" id="contact-form" className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="font-heading text-lg text-foreground-900 mb-5">在线留言</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 *</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">电话</label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                        placeholder="请输入您的电话号码"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 *</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      autoComplete="off"
                      tabIndex={-1}
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center font-medium">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formMessage}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}